import { Autocomplete, FilterOptionsState, TextField } from '@mui/material';
import {
  ExIconButton,
  ExLoader,
  ExPill,
  ExTooltip,
  IconButtonFlavor,
  IconButtonSize,
  IconButtonType,
  LoaderSize,
  LoaderVariant,
  PillColor,
  PillSize,
  TooltipAlignment,
  TooltipPosition,
} from '@boomi/exosphere';
import { useTranslation } from 'react-i18next';
import {
  forwardRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import Constants from '../../pages/BoomiAI/AppConstants';
import CustomDefaultPaper, { PopperBottom, PopperTop } from '../../pages/BoomiAI/autoCompleteHelper';
import processCall from '@assets/boomi-ai/ui-kit/process.svg';
import apiService from '../../pages/BoomiAI/api-service';
import AddLinkDialog from '../AddLinkDialog/AddLinkDialog';
import { useBoomiContext } from '../../context/BoomiContext';
import sendBtn_dark from '@assets/boomi-ai/ui-kit/landingpage_send-btn_dark.svg';
import sendBtn_light from '@assets/boomi-ai/ui-kit/sendBtnLight.svg';
import sendBtnDisable_dark from '@assets/boomi-ai/ui-kit/chatpage_send-btn_dark.svg';
import sendBtnDisable from '@assets/boomi-ai/ui-kit/chatpage_send-btn_light.svg';

function AutocompleteHandler(props: {
  enterText: any,
  disableChat: any,
  disabled: any,
  isDarkMode: boolean,
  getUserProcesses: any,
  inputRef: any,
  handleFocus: any,
  bottomEl: any,
  setHeight: any,
  conversationPage: boolean,
  placeHolderType: string,
  methodResponse: any,
  disableInteraction: any,
}, ref: any) {
  const {
    enterText, disabled, isDarkMode, getUserProcesses, inputRef, handleFocus, bottomEl,
    setHeight, conversationPage, placeHolderType, methodResponse,
    disableInteraction = false,
  } = props;
  const { inputText, setInputText } = useBoomiContext();
  interface Process {
    label: string;
    componentId: string;
    componentID: string;
    version?: number;
    name: string;
    type: string;
    deleted: string;
    folderName: string;
    value: string;
    queryToken: string;
  }
  const navigate = useNavigate();
  const autoCompleteRef = useRef();
  const [open, setOpen] = useState(false);
  const [muiInput, setMuiInput] = useState(inputText);
  const [selectedMuiOptionValue, setSelectedMuiOptionValue] = useState('');
  const [intents, setIntents] = useState<Process[]>([]);
  const [processDetails, setProcessDetails] = useState<Process[]>([]);
  const [suggestions, setSuggestions] = useState<Process[]>([]);
  const aiInputValue = useRef('');
  const [t] = useTranslation('common');
  const [showAddLinkDialog, setShowAddLinkDialog] = useState(false);
  const [areMoreProcesses, setAreMoreProcesses] = useState(false);
  const [processQueryString, setProcessQueryString] = useState('');
  const [scrollPosition, setScrollPosition] = useState(0);
  const [disableScroll, setDisableScroll] = useState(false);
  const [isApiInProgress, setIsApiInProgress] = useState(false);
  const linkInputRef = useRef<HTMLInputElement | null>(null);
  const isAgentFoundation = window.location.href.includes('home');
  const [process, setProcess] = useState<Process>({
    label: '',
    componentId: '',
    componentID: '',
    version: 0,
    name: '',
    type: '',
    deleted: '',
    folderName: '',
    value: '',
    queryToken: '',
  });
  useImperativeHandle(ref, () => ({
    updateText(text: any) {
      setMuiInput(text);
    },
  }));

  useEffect(() => {
    function loadPreDefinedIntents() {
      if (!intents.length) {
        apiService.getPreDefinedIntents()
          .then((response: any) => {
            if (Array.isArray(response)) {
              setIntents(response);
              setSuggestions(response);
            }
          });
      }
    }

    loadPreDefinedIntents();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    setMuiInput(inputText);
  }, [inputText]);

  function removeProcessAttachment(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Perform synchronous operations
        setProcess({
          componentId: '',
          componentID: '',
          deleted: '',
          folderName: '',
          label: '',
          name: '',
          type: '',
          version: 0,
          value: '',
          queryToken: '',
        });
        // Resolve the promise to indicate completion
        resolve();
      } catch (error) {
        // Reject the promise if there's an error
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }
  const setBottomDivHeight = () => {
    if (bottomEl.current) {
      const footerElement = bottomEl.current;
      const footerHeight = footerElement ? footerElement.offsetHeight : 0; // Get footer height
      setHeight(footerHeight + 64);
    }
  };

  useEffect(() => {
    if (bottomEl.current) {
      setBottomDivHeight();
    }
    // disabling since the height of the bottomDiv needs to be recalculated only
    // when process is added/removed
    // eslint-disable-next-line
  }, [process]);

  const handleBlur = () => {
    // Check if the inputRef is set and if it has focus
    if (inputRef.current) {
      setOpen(false);
    }
  };

  const handleScroll = async (event: any) => {
    const bottom = ((event.target.scrollHeight
        - event.target.scrollTop) - event.target.clientHeight);
    // bottom <=1 instead of true/false earlier is when user sets the zoom of
    // the browser more below or above 100%, the calculation of value for
    // bottom differs between .25 - .50 BI-5025
    if (bottom <= 1 && areMoreProcesses) {
      setIsApiInProgress(true);
      setScrollPosition(event.target.scrollTop);
      setDisableScroll(true);
      const searchResult = await getUserProcesses(
        processQueryString,
        Constants.PROCESS,
        areMoreProcesses,
      );
      if (searchResult?.results) {
        setProcessDetails((prev: any) => [...prev, ...searchResult.results]);
        setSuggestions((prev: any) => [...prev, ...searchResult.results]);
        setAreMoreProcesses(searchResult.queryToken);
      }
      setDisableScroll(false);
      setIsApiInProgress(false);
    }
  };

  function removeHashFromString(inputString: string) {
    const hashIndex = inputString.indexOf('#');
    if (hashIndex !== -1) {
      return inputString.substring(0, hashIndex);
    }
    return inputString; // Return the original string if no hash character is found
  }

  async function handleselect(e: any, option: any) {
    if (option && (option?.current || option?.label || option?.name)) {
      methodResponse.current = ('Search-suggestion');
      const processInputText = option?.current ? option.current : option;
      const nonProcessInput = option?.current || option?.label;
      if (muiInput.includes('#')) {
        await removeProcessAttachment();
        setProcess(processInputText);
        setMuiInput(removeHashFromString(muiInput));
        setSuggestions(intents);
        aiInputValue.current = '';
      } else {
        if (isAgentFoundation) {
          setInputText(nonProcessInput);
          navigate('/chat');
        }
        enterText(nonProcessInput, process);
        setMuiInput('');
        aiInputValue.current = '';
      }
      setOpen(false);
    }
  }

  const handleHighlightChange = (event: any, option: any, reason: any) => {
    if (option && (reason === 'keyboard' || reason === 'mouse')) {
      aiInputValue.current = option.label || option;
    }
  };

  const extractUrls = (text: any) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.match(urlRegex) || [];
  };

  const removeUrls = (text: any) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.replace(urlRegex, '');
  };

  // handle the content Pasted in the search input
  const handlePaste = async (event: any) => {
    setSuggestions([]);
    const pastedText = event.clipboardData.getData('text/plain');
    const isURL = extractUrls(pastedText.trim());
    if (isURL.length) {
      await removeProcessAttachment();
      setIsApiInProgress(true);
      const textWithoutUrl = removeUrls(pastedText.trim());
      apiService.isValidUrl(isURL[0], Constants.PROCESS)
        .then((response: any) => {
          if (response.success) {
            setProcess(response.results);
            // Get the current selection range
            const { selectionStart } = event.target;
            // Update the input value with the pasted text removed
            if (textWithoutUrl.length) setMuiInput(textWithoutUrl);
            else setMuiInput(muiInput + textWithoutUrl);
            // Move the cursor to the position after the pasted text
            event.target.selectionEnd = selectionStart;
            event.target.selectionStart = event.target.selectionEnd;
          } else setMuiInput(muiInput + pastedText);
        })
        .finally(() => {
          setIsApiInProgress(false);
          setSuggestions(intents);
        });
      // Perform actions for URL
    } else {
      setSuggestions(intents);
      // eslint-disable-next-line
      console.log('Pasted text is not a URL:', pastedText);
      // Perform actions for non-URL
    }
  };
  const handleChangeForHashValue = async (e: any, isHash: any) => {
    setSuggestions([]);
    setIsApiInProgress(true);
    const searchText = e.target.value.split('#')[1] ? e.target.value.split('#')[1] : '';
    setProcessQueryString(searchText);
    const searchResult = await getUserProcesses(searchText, Constants.PROCESS, '');
    if (searchResult?.results && isHash) {
      setProcessDetails(searchResult?.results);
      setSuggestions(searchResult.results);
      setAreMoreProcesses(searchResult.queryToken);
      setOpen(true);
      setDisableScroll(false);
    }
    setIsApiInProgress(false);
  };
  const getConditionResult = (e: any, input: string) => (
    (e.target.value === '' || e.target.value === undefined || e.target.value === null || e.type === Constants.KEY_CODE.BLUR) && (input === '' || input === undefined)
  );
  const handleChangeForKeyCodeEnter = (e: any) => {
    methodResponse.current = 'Search-keyboard-enter';
    if (aiInputValue?.current.length) {
      setMuiInput(aiInputValue.current);
      setOpen(false);
      if (isAgentFoundation) {
        setInputText(aiInputValue.current);
        navigate('/chat');
      }
      enterText(aiInputValue.current, process);
    } else {
      e.preventDefault();
      setOpen(false);
      if (isAgentFoundation) {
        setInputText(muiInput);
        navigate('/chat');
      }
      enterText(muiInput, process);
      setMuiInput('');
      setProcess({
        componentID: '',
        componentId: '',
        deleted: '',
        folderName: '',
        label: '',
        name: '',
        queryToken: '',
        type: '',
        value: '',
        version: 0,
      });
    }
  };

  const handleChange = async (e:any) => {
    if (e?.nativeEvent.inputType === 'insertFromPaste') return;
    const isHash = e.target.value.includes('#');
    const condition = getConditionResult(e, muiInput);
    const value = e?.target?.value;
    if (value?.includes('#') && e.key !== Constants.KEY_CODE.ENTER) {
      handleChangeForHashValue(e, isHash);
    } else if (condition) {
      setOpen(false);
      e.preventDefault();
    } else if (e.type === Constants.KEY_CODE.CHANGE) {
      setSuggestions(intents);
      if (e.target.value.length >= 3) {
        setOpen(true);
        setSuggestions(intents);
      } else setOpen(false);
    } else if (e.type === Constants.KEY_CODE.CLICK) {
      if (isHash) return;
      setOpen(false);
      if (isAgentFoundation) {
        setInputText(e.target.innerText);
        navigate('/chat');
      }
      enterText(e.target.innerText, process);
    } else if (e.key === Constants.KEY_CODE.ENTER) {
      handleChangeForKeyCodeEnter(e);
    }
  };

  const filterOptions = (filterOption: any[], state: FilterOptionsState<any>) => {
    const { inputValue: value } = state;
    if (value.length < 3 && !value.includes('#')) {
      // Return an empty array if less than 3 characters are entered
      return [];
    }
    const isHash = value.includes('#');
    if (isHash && processDetails.length > 0) return filterOption;

    if (process.name) return [];
    const searchText = value.includes('#') ? value.split('#')[1] : value;
    if (searchText.trim().length === 0 && !isHash) return [];
    return filterOption.filter((option: any) => {
      const nameMatch = option.name && option.name.toLowerCase().includes(searchText.toLowerCase());
      const labelMatch = option.label
          && option.label.toLowerCase().includes(searchText.toLowerCase());

      return nameMatch || labelMatch;
      // Exclude the option otherwise
    }).filter((option: any, index: any) => (option.label
            && option.label.toLowerCase().includes(searchText.toLowerCase()) && index < 5)
        || (option.name && option.name.toLowerCase().includes(searchText.toLowerCase())));
  };

  const getOptionLabel = (option: any) => option?.label || option?.name || option;

  function sendButtonPressed() {
    if (muiInput.trim().length > 0) {
      if (isAgentFoundation) {
        setInputText(muiInput);
        navigate('/chat');
      }
      methodResponse.current = 'Search-send-button';
      enterText(muiInput, process);
      setMuiInput('');
      setProcess({
        componentID: '',
        componentId: '',
        deleted: '',
        folderName: '',
        label: '',
        name: '',
        queryToken: '',
        type: '',
        value: '',
        version: 0,
      });
    }
  }

  const handleLinkDialog = () => {
    setShowAddLinkDialog(true);
    document.body.style.overflow = 'unset';
    const linkButtonElement = document.querySelector('.add-link-btn');
    if (linkButtonElement instanceof HTMLButtonElement) {
      linkButtonElement.blur();
    }
    if (linkInputRef.current) {
      // Focus on the input element of the dialog
      const timerId = setTimeout(() => linkInputRef?.current?.focus(), 300);
      // Cleanup function to cancel the timeout if the component unmounts
      return () => clearTimeout(timerId);
    }
    return undefined;
  };

  useEffect(() => {
    if (showAddLinkDialog) {
      handleLinkDialog();
    }
  }, [showAddLinkDialog]);

  const updateProcessId = (resetProcess: any) => {
    setProcess({
      componentId: resetProcess.componentId,
      deleted: resetProcess.deleted,
      folderName: resetProcess.folderName,
      componentID: resetProcess?.componentID,
      label: '',
      name: resetProcess.name,
      type: resetProcess.type,
      version: resetProcess.version,
      value: resetProcess.value,
      queryToken: resetProcess.queryToken,
    });
  };

  return (
    <>
      {!isApiInProgress && (
      <ExIconButton
        icon="plus-circle"
        className={process?.name ? 'add-link-btn add-link__process-added' : 'add-link-btn'}
        disabled={disabled}
        type={IconButtonType.TERTIARY}
        size={IconButtonSize.DEFAULT}
        flavor={IconButtonFlavor.BRANDED}
        hideBrowserTooltip
        tooltipText="Add link"
        onClick={handleLinkDialog}
        data-testid="boomi-gpt-add-link-button"
      />
      )}
      <Autocomplete
        ref={autoCompleteRef}
        open={open}
        onClose={() => setOpen(false)}
        id="combo-box-demo"
        freeSolo
        slotProps={{
          popper: {
            sx: {
              zIndex: 898, // SideDrawerZIndex - 1
            },
          },
        }}
        onScroll={handleScroll}
        disabled={disabled || disableInteraction}
        options={suggestions}
        getOptionLabel={getOptionLabel}
        fullWidth
        inputValue={muiInput}
        value={muiInput}
        autoHighlight={false}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            if (event.shiftKey) return;
            if (event.ctrlKey) return;
            if (aiInputValue.current && !process.name) {
              handleselect(event, aiInputValue);
            } else handleChange(event);
          }
        }}
        isOptionEqualToValue={(option) => (option.label === selectedMuiOptionValue)}
        onChange={(e, value: any) => {
          if (e.type === Constants.KEY_CODE.CLICK) {
            if (value) {
              setSelectedMuiOptionValue(value.label);
            } handleselect(e, value);
          }
        }}
        onHighlightChange={handleHighlightChange}
        filterOptions={filterOptions}
        PaperComponent={(paperProps) => (
          CustomDefaultPaper(paperProps, isDarkMode, handleScroll, scrollPosition, disableScroll)
        )}
        PopperComponent={conversationPage ? PopperTop : PopperBottom}
        renderInput={(params) => (
          <TextField
                    {...params} // eslint-disable-line
            placeholder={placeHolderType === Constants.DAG ? t('dag_input_text_placeholder') : t('ai_input_text_placeholder')}
            value={muiInput}
            onChange={(e: any) => {
              if (e !== null && (e.type === Constants.KEY_CODE.CHANGE)) {
                setMuiInput(e.target.value);
                handleChange(e);
              }
            }}
            InputProps={{
              ...params.InputProps,
              sx: {
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                textarea: {
                  alignItems: 'center', // horizontal centering
                  paddingTop: '10px', // tweak if needed
                },
              },
              endAdornment: (
              // eslint-disable-next-line react/jsx-no-useless-fragment
                <>
                  { process?.name && (
                    <div className="process_pill_wrapper">
                      <div className="selected-process">
                        <ExPill
                          interactive
                          removable
                          size={PillSize.REGULAR}
                          color={PillColor.BLUE}
                          onClick={(e: any) => {
                            e.stopPropagation();
                          }}
                          onRemove={removeProcessAttachment}
                          wrap
                        >
                          {process.deleted === Constants.TRUE && (
                            Constants.DELETED
                          )}
                          {' '}
                          {process.name}
                        </ExPill>
                      </div>
                    </div>
                  )}
                </>
              ),
            }}
            multiline
            onFocus={handleFocus}
            inputRef={inputRef}
            onBlur={handleBlur}
            label=""
            onPaste={(e: any) => {
              handlePaste(e);
            }}
            sx={{
              boxShadow: 'none', '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 0 }, '& fieldset': { border: 'none' }, input: { fontSize: 16 },
            }}
          />
        )}
        renderOption={(renderProps, option: any) => (
          <li
                    // eslint-disable-next-line react/jsx-props-no-spreading
            {...renderProps}
            key={option?.name ? (option.componentID
                        || option?.componentId) : option.label}
          >
            { option?.label && (
              option.label
            )}
            { option?.name && (
              <div className="autocomplete-wrapper">
                <div className="process-details">
                  <span className="process-call-image">
                    <img src={processCall} alt="process" />
                  </span>
                  <span className="process-name">{option.name}</span>
                </div>
                <div className="process-location">
                  {option.folderName}
                </div>
              </div>
            )}
          </li>
        )}
      />
      {isApiInProgress && (
      <ExLoader size={LoaderSize.MEDIUM} variant={LoaderVariant.SPINNER} inline className="gpt-loader" />
      )}
      {
        showAddLinkDialog
        && (
        <AddLinkDialog
          updateProcessId={updateProcessId}
          linkInputRef={linkInputRef}
          inputRef={inputRef}
          showAddLinkDialog={showAddLinkDialog}
          setShowAddLinkDialog={setShowAddLinkDialog}
        />
        )
      }
      <>
        {
            isDarkMode && muiInput?.length > 0 && (
              <div>
                <ExTooltip
                  tooltipDelay={0}
                  position={TooltipPosition.TOP}
                  alignment={TooltipAlignment.MIDDLE}
                >
                  <div style={{ display: 'flex' }}>
                    {t('chat_send_button_tooltip')}
                  </div>
                  <button slot="anchor" type="button" data-testid="boomi-gpt-chat-send-button" title="Send" className={`send-btn ${process?.name ? 'chat-send__process-added' : ''} ${isAgentFoundation ? 'mini-gpt' : ''}`} aria-label="Send" onClick={sendButtonPressed}>
                    <img className="chat-send-btn" alt="Send" src={sendBtn_dark} />
                    {/* <div className="overlay_dark" /> */}
                  </button>
                </ExTooltip>
              </div>
            )
        }
        { !isDarkMode && muiInput?.length > 0 && (
          <div>
            <ExTooltip
              tooltipDelay={0}
              position={TooltipPosition.TOP}
              alignment={TooltipAlignment.MIDDLE}
            >
              <div style={{ display: 'flex' }}>
                {t('chat_send_button_tooltip')}
              </div>
              <button type="button" slot="anchor" data-testid="boomi-gpt-chat-send-button" title="Send" className={`send-btn ${process?.name ? 'chat-send__process-added' : ''} ${isAgentFoundation ? 'mini-gpt' : ''}`} aria-label="Send" onClick={sendButtonPressed}>
                <img className="chat-send-btn" alt="Send" src={sendBtn_light} />
                {/* <div className="overlay" /> */}
              </button>
            </ExTooltip>
          </div>
        )}
        {
            isDarkMode && !muiInput?.length
            && (
            <button type="button" data-testid="boomi-gpt-chat-send-button" className={`send-btn ${process?.name ? 'chat-send__process-added' : ''} ${isAgentFoundation ? 'mini-gpt' : ''}`} aria-label="Send">
              <img className="chat-send-btn" alt="" src={sendBtnDisable_dark} />
            </button>
            )
        }
        {
            !isDarkMode && !muiInput?.length
            && (
            <button type="button" data-testid="boomi-gpt-chat-send-button" className={`send-btn ${process?.name ? 'chat-send__process-added' : ''} ${isAgentFoundation ? 'mini-gpt' : ''}`} aria-label="Send">
              <img className="chat-send-btn disabled" alt="" src={sendBtnDisable} />
            </button>
            )
        }
      </>
    </>
  );
}
export default forwardRef(AutocompleteHandler);
export interface AutoCompleteRef {
  updateText: (text: any) => void;
}
